import {
  createPackageFn,
  deletePackageFn,
  listAllPackagesOptions,
  listPublicPackagesOptions,
  toggleActiveFn,
  toggleRecommendedFn,
  updatePackageFn,
} from "@/actions/coaching-package/handler";
import {
  PackageType,
  type SelectCoachingPackage,
  getPackageTypeName,
} from "@/api/db/schema/coaching-package";
import { PackageForm } from "@/components/admin/PackageForm";
import { PackageList } from "@/components/admin/PackageList";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  redirect,
  useRouteContext,
} from "@tanstack/react-router";
import { ArrowLeft, ContactRound, Plus, Users } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/admin/packages")({
  beforeLoad: async ({ context }) => {
    if (!context.user || context.user.role !== "admin") {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context }) =>
    await context.queryClient.ensureQueryData(listAllPackagesOptions()),
  component: RouteComponent,
});

function RouteComponent() {
  const { data } = useSuspenseQuery(listAllPackagesOptions());
  const { queryClient } = useRouteContext({ from: "/_app/admin/packages" });

  const [activeTab, setActiveTab] = useState<"coaching" | "consulting">(
    "coaching",
  );
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPackage, setEditingPackage] =
    useState<SelectCoachingPackage | null>(null);
  const [deletePackage, setDeletePackage] =
    useState<SelectCoachingPackage | null>(null);

  // Filter packages by type
  const coachingPackages =
    data.body?.packages?.filter(
      (p) => p.packageType === PackageType.COACHING,
    ) || [];
  const consultingPackages =
    data.body?.packages?.filter(
      (p) => p.packageType === PackageType.CONSULTING,
    ) || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: createPackageFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAllPackagesOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: listPublicPackagesOptions().queryKey,
      });
      toast.success("Paket başarıyla oluşturuldu");
      setIsFormOpen(false);
      setEditingPackage(null);
    },
    onError: (error) => {
      toast.error("Paket oluşturulamadı", {
        description: error.message,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: updatePackageFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAllPackagesOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: listPublicPackagesOptions().queryKey,
      });
      toast.success("Paket başarıyla güncellendi");
      setIsFormOpen(false);
      setEditingPackage(null);
    },
    onError: (error) => {
      toast.error("Paket güncellenemedi", {
        description: error.message,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deletePackageFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAllPackagesOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: listPublicPackagesOptions().queryKey,
      });
      toast.success("Paket başarıyla silindi");
      setDeletePackage(null);
    },
    onError: (error) => {
      toast.error("Paket silinemedi", {
        description: error.message,
      });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: toggleActiveFn,
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: listAllPackagesOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: listPublicPackagesOptions().queryKey,
      });
      toast.success(response.body?.message || "Durum değiştirildi");
    },
    onError: (error) => {
      toast.error("Durum değiştirilemedi", {
        description: error.message,
      });
    },
  });

  const toggleRecommendedMutation = useMutation({
    mutationFn: toggleRecommendedFn,
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: listAllPackagesOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: listPublicPackagesOptions().queryKey,
      });
      toast.success(response.body?.message || "Öneri durumu değiştirildi");
    },
    onError: (error) => {
      toast.error("Öneri durumu değiştirilemedi", {
        description: error.message,
      });
    },
  });

  // Handlers
  const handleCreate = (packageType: PackageType) => {
    setEditingPackage(null);
    setActiveTab(
      packageType === PackageType.COACHING ? "coaching" : "consulting",
    );
    setIsFormOpen(true);
  };

  const handleEdit = (pkg: SelectCoachingPackage) => {
    setEditingPackage(pkg);
    setIsFormOpen(true);
  };

  const handleDelete = (pkg: SelectCoachingPackage) => {
    setDeletePackage(pkg);
  };

  const handleToggleActive = (pkg: SelectCoachingPackage) => {
    toggleActiveMutation.mutate({ data: { packageId: pkg.packageId } });
  };

  const handleToggleRecommended = (pkg: SelectCoachingPackage) => {
    toggleRecommendedMutation.mutate({ data: { packageId: pkg.packageId } });
  };

  const handleFormSubmit = async (formData: {
    packageType: PackageType;
    data: any;
    displayOrder?: number;
  }) => {
    if (editingPackage) {
      await updateMutation.mutateAsync({
        data: {
          packageId: editingPackage.packageId,
          ...formData,
        },
      });
    } else {
      await createMutation.mutateAsync({ data: formData });
    }
  };

  const handleConfirmDelete = () => {
    if (deletePackage) {
      deleteMutation.mutate({ data: { packageId: deletePackage.packageId } });
    }
  };

  const currentPackageType =
    activeTab === "coaching" ? PackageType.COACHING : PackageType.CONSULTING;

  return (
    <div className="container mx-auto max-w-7xl space-y-8 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      {/* Breadcrumb */}
      <div className="mb-6">
        <Button asChild variant="ghost" className="mb-4">
          <Link to="/admin">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Admin Paneli
          </Link>
        </Button>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-bold text-2xl sm:text-3xl">Paket Yönetimi</h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            Koçluk ve danışmanlık paketlerini yönetin
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="coaching" className="flex-1 gap-2 sm:flex-none">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">
                {getPackageTypeName(PackageType.COACHING)}
              </span>
              <span className="sm:hidden">Koçluk</span>
              <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs">
                {coachingPackages.length}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="consulting"
              className="flex-1 gap-2 sm:flex-none"
            >
              <ContactRound className="h-4 w-4" />
              <span className="hidden sm:inline">
                {getPackageTypeName(PackageType.CONSULTING)}
              </span>
              <span className="sm:hidden">Danışmanlık</span>
              <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs">
                {consultingPackages.length}
              </span>
            </TabsTrigger>
          </TabsList>

          <Button
            onClick={() => handleCreate(currentPackageType)}
            className="w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">
              Yeni {getPackageTypeName(currentPackageType)} Paketi
            </span>
            <span className="sm:hidden">Yeni Paket</span>
          </Button>
        </div>

        <TabsContent value="coaching" className="mt-6">
          <PackageList
            packages={coachingPackages}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            onToggleRecommended={handleToggleRecommended}
            isLoading={{
              delete: deleteMutation.isPending
                ? deletePackage?.packageId
                : undefined,
              toggleActive: toggleActiveMutation.isPending
                ? toggleActiveMutation.variables?.data.packageId
                : undefined,
              toggleRecommended: toggleRecommendedMutation.isPending
                ? toggleRecommendedMutation.variables?.data.packageId
                : undefined,
            }}
          />
        </TabsContent>

        <TabsContent value="consulting" className="mt-6">
          <PackageList
            packages={consultingPackages}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            onToggleRecommended={handleToggleRecommended}
            isLoading={{
              delete: deleteMutation.isPending
                ? deletePackage?.packageId
                : undefined,
              toggleActive: toggleActiveMutation.isPending
                ? toggleActiveMutation.variables?.data.packageId
                : undefined,
              toggleRecommended: toggleRecommendedMutation.isPending
                ? toggleRecommendedMutation.variables?.data.packageId
                : undefined,
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPackage ? "Paketi Düzenle" : "Yeni Paket Oluştur"}
            </DialogTitle>
          </DialogHeader>
          <PackageForm
            initialData={editingPackage || undefined}
            packageType={currentPackageType}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setIsFormOpen(false);
              setEditingPackage(null);
            }}
            isLoading={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deletePackage}
        onOpenChange={(open) => !open && setDeletePackage(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Paketi Sil</AlertDialogTitle>
            <AlertDialogDescription>
              {deletePackage?.data.title} paketini silmek istediğinize emin
              misiniz? Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              İptal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
